'use client';
import React, { createContext, useContext, useState, useEffect } from 'react';

export type AltitudeUnit = 'm' | 'ft';
export type VzUnit = 'm/s' | 'kts';
export type WindUnit = 'km/h' | 'kts';
export type TempUnit = '°C' | '°F';
export type AppTheme = 'night' | 'velivole';

export interface AppSettings {
  altitudeUnit: AltitudeUnit;
  vzUnit: VzUnit;
  windUnit: WindUnit;
  tempUnit: TempUnit;
  theme: AppTheme;
  safetyMargin: number; // metres — subtracted from ceiling before glide calc
}

const DEFAULT_SETTINGS: AppSettings = {
  altitudeUnit: 'm',
  vzUnit: 'm/s',
  windUnit: 'km/h',
  tempUnit: '°C',
  theme: 'night',
  safetyMargin: 300,
};

const LS_SETTINGS_KEY = 'soarcast_settings';
const LS_ICAO_HISTORY_KEY = 'soarcast_icao_history';

interface SettingsContextValue {
  settings: AppSettings;
  updateSettings: (patch: Partial<AppSettings>) => void;
  icaoHistory: string[];
  addIcaoHistory: (icao: string) => void;
  removeIcaoHistory: (icao: string) => void;
  // Conversion helpers
  formatAltitude: (meters: number) => { primary: string; secondary: string; primaryUnit: string; secondaryUnit: string };
  formatVZ: (ms: number) => { primary: string; secondary: string; primaryUnit: string; secondaryUnit: string };
  formatWind: (kmh: number) => { primary: string; secondary: string; primaryUnit: string; secondaryUnit: string };
  formatTemp: (celsius: number) => { primary: string; secondary: string; primaryUnit: string; secondaryUnit: string };
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: DEFAULT_SETTINGS,
  updateSettings: () => {},
  icaoHistory: [],
  addIcaoHistory: () => {},
  removeIcaoHistory: () => {},
  formatAltitude: (m) => ({ primary: m.toString(), secondary: '', primaryUnit: 'm', secondaryUnit: 'ft' }),
  formatVZ: (v) => ({ primary: v.toString(), secondary: '', primaryUnit: 'm/s', secondaryUnit: 'kts' }),
  formatWind: (w) => ({ primary: w.toString(), secondary: '', primaryUnit: 'km/h', secondaryUnit: 'kts' }),
  formatTemp: (t) => ({ primary: t.toString(), secondary: '', primaryUnit: '°C', secondaryUnit: '°F' }),
});

function loadSettings(): AppSettings {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  try {
    const raw = localStorage.getItem(LS_SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function loadIcaoHistory(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LS_ICAO_HISTORY_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [icaoHistory, setIcaoHistory] = useState<string[]>([]);

  useEffect(() => {
    setSettings(loadSettings());
    setIcaoHistory(loadIcaoHistory());
  }, []);

  function updateSettings(patch: Partial<AppSettings>) {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      try { localStorage.setItem(LS_SETTINGS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  function addIcaoHistory(icao: string) {
    setIcaoHistory((prev) => {
      const filtered = prev.filter((c) => c !== icao);
      const next = [icao, ...filtered].slice(0, 10);
      try { localStorage.setItem(LS_ICAO_HISTORY_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  function removeIcaoHistory(icao: string) {
    setIcaoHistory((prev) => {
      const next = prev.filter((c) => c !== icao);
      try { localStorage.setItem(LS_ICAO_HISTORY_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  // Altitude: m ↔ ft
  function formatAltitude(meters: number): { primary: string; secondary: string; primaryUnit: string; secondaryUnit: string } {
    const ft = Math.round(meters * 3.281);
    const m = Math.round(meters);
    if (settings.altitudeUnit === 'ft') {
      return { primary: ft.toLocaleString('fr'), secondary: m.toLocaleString('fr'), primaryUnit: 'ft', secondaryUnit: 'm' };
    }
    return { primary: m.toLocaleString('fr'), secondary: ft.toLocaleString('fr'), primaryUnit: 'm', secondaryUnit: 'ft' };
  }

  // VZ: m/s ↔ kts (1 m/s = 1.944 kts)
  function formatVZ(ms: number): { primary: string; secondary: string; primaryUnit: string; secondaryUnit: string } {
    const kts = (ms * 1.944).toFixed(1);
    const mps = ms.toFixed(1);
    if (settings.vzUnit === 'kts') {
      return { primary: kts, secondary: mps, primaryUnit: 'kts', secondaryUnit: 'm/s' };
    }
    return { primary: mps, secondary: kts, primaryUnit: 'm/s', secondaryUnit: 'kts' };
  }

  // Wind: km/h ↔ kts (1 km/h = 0.5400 kts)
  function formatWind(kmh: number): { primary: string; secondary: string; primaryUnit: string; secondaryUnit: string } {
    const kts = Math.round(kmh * 0.5400);
    const kph = Math.round(kmh);
    if (settings.windUnit === 'kts') {
      return { primary: kts.toString(), secondary: kph.toString(), primaryUnit: 'kts', secondaryUnit: 'km/h' };
    }
    return { primary: kph.toString(), secondary: kts.toString(), primaryUnit: 'km/h', secondaryUnit: 'kts' };
  }

  // Temp: °C ↔ °F
  function formatTemp(celsius: number): { primary: string; secondary: string; primaryUnit: string; secondaryUnit: string } {
    const f = Math.round(celsius * 9 / 5 + 32);
    const c = Math.round(celsius);
    if (settings.tempUnit === '°F') {
      return { primary: f.toString(), secondary: c.toString(), primaryUnit: '°F', secondaryUnit: '°C' };
    }
    return { primary: c.toString(), secondary: f.toString(), primaryUnit: '°C', secondaryUnit: '°F' };
  }

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, icaoHistory, addIcaoHistory, removeIcaoHistory, formatAltitude, formatVZ, formatWind, formatTemp }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextValue {
  return useContext(SettingsContext);
}
